import React, { useEffect, useState, useCallback } from 'react';
import { Download, Share, X, Plus } from 'lucide-react';
import { Button } from './ui/button';

// localStorage flags — dismissals are lightweight and privacy-safe.
const DISMISS_KEY = 'lutrom.pwa.install-dismissed';
const INSTALLED_KEY = 'lutrom.pwa.installed';
// How long to hide the banner after a user taps "Not now".
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  // iOS uses `navigator.standalone`; everyone else exposes it via the
  // `display-mode: standalone` media query.
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

const isIos = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  // iPadOS 13+ reports as Mac — disambiguate with a touch check so we don't
  // nag desktop Safari users.
  const iPadOs = /Macintosh/.test(ua) && 'ontouchend' in document;
  return /iPhone|iPad|iPod/.test(ua) || iPadOs;
};

const shouldShowFromDismissal = () => {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || '0');
    return !ts || Date.now() - ts > DISMISS_TTL_MS;
  } catch { return true; }
};

/**
 * Subtle bottom banner that invites the user to "install" the portal on
 * their home screen. Three modes:
 *  - Chromium/Android: uses the captured `beforeinstallprompt` event so
 *    tapping "Install" triggers the native prompt.
 *  - iOS Safari: no programmatic prompt exists, so we show the canonical
 *    "Tap Share → Add to Home Screen" hint.
 *  - Already installed / desktop: renders nothing.
 *
 * Dismissals persist in localStorage for 14 days so the banner stays
 * out of the way once acknowledged.
 */
const PwaInstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;        // already launched as an app
    if (!shouldShowFromDismissal()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();              // keep the native mini-infobar hidden
      setDeferredPrompt(e);
      setVisible(true);
    };
    const onInstalled = () => {
      try { localStorage.setItem(INSTALLED_KEY, '1'); } catch { /* ignore */ }
      setVisible(false);
      setShowIosHint(false);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    // iOS Safari path: the event above never fires. Surface the hint with
    // a small delay so it doesn't flash during the initial route render.
    if (isIos()) {
      const t = setTimeout(() => setVisible(true), 1500);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', onBeforeInstall);
        window.removeEventListener('appinstalled', onInstalled);
      };
    }
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
    setShowIosHint(false);
  }, []);

  const install = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setVisible(false);
      if (outcome !== 'accepted') {
        // User cancelled the native prompt — respect their choice, but
        // don't punish them with the full 14d cooldown either.
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
      }
    } else if (isIos()) {
      setShowIosHint(true);
    }
  }, [deferredPrompt]);

  if (!visible) return null;

  // iOS expanded-hint state — walks the user through Share → Add to Home Screen.
  if (showIosHint) {
    return (
      <div
        className="fixed inset-x-3 bottom-3 z-50 max-w-md mx-auto rounded-xl bg-slate-900 text-white shadow-2xl border border-slate-700 p-4"
        data-testid="pwa-install-ios-hint"
      >
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <div className="font-semibold text-sm mb-2">Install Lutrom on this iPad/iPhone</div>
            <ol className="text-xs text-slate-200 space-y-1.5 list-decimal list-inside">
              <li className="flex items-center gap-2">
                Tap the <Share className="inline h-3.5 w-3.5 mx-0.5" aria-hidden /> Share icon at the bottom of Safari
              </li>
              <li className="flex items-center gap-2">
                Scroll down and tap <Plus className="inline h-3.5 w-3.5 mx-0.5" aria-hidden /> "Add to Home Screen"
              </li>
              <li>Tap "Add" to pin the Lutrom icon</li>
            </ol>
          </div>
          <button
            onClick={dismiss}
            className="text-slate-400 hover:text-white transition-colors"
            aria-label="Close install hint"
            data-testid="pwa-install-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // Default compact banner — Android/Chrome shows "Install"; iOS shows
  // "Install" but it expands into the hint card on click.
  return (
    <div
      className="fixed inset-x-3 bottom-3 z-50 max-w-md mx-auto rounded-xl bg-slate-900 text-white shadow-2xl border border-slate-700"
      data-testid="pwa-install-banner"
    >
      <div className="flex items-center gap-3 p-3">
        <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
          <Download className="h-5 w-5 text-blue-300" aria-hidden />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold leading-tight">Install Lutrom Portal</div>
          <div className="text-[11px] text-slate-300 leading-tight mt-0.5 truncate">
            One-tap launch from your home screen
          </div>
        </div>
        <Button
          onClick={install}
          size="sm"
          className="bg-blue-600 hover:bg-blue-500 text-white h-8 px-3"
          data-testid="pwa-install-btn"
        >
          Install
        </Button>
        <button
          onClick={dismiss}
          className="text-slate-400 hover:text-white transition-colors p-1"
          aria-label="Not now"
          data-testid="pwa-install-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default PwaInstallBanner;
